import { ParagraphSkeletonLoading, getSize, getColor } from '@lib/common';
import { Headings, Questions } from '@module/form';
import styled from 'styled-components';
import { SectionItemComponent } from './components';
import { HeaderComponent } from './header.component';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SidebarLiveDragProvider, useSidebarLiveDrag } from './sidebar-live-drag.context';
import { useDragAutoScroll } from './hooks/use-drag-auto-scroll';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var Layout = styled.div.withConfig({
  displayName: "section-summary-v2component__Layout",
  componentId: "sc-zf6xv1-0"
})(["display:flex;flex-direction:column;height:100%;min-height:0;overflow:hidden;background-color:", ";"], getColor('fillBgWhite'));
var StyledSectionSummaryWrapper = styled.div.withConfig({
  displayName: "section-summary-v2component__StyledSectionSummaryWrapper",
  componentId: "sc-zf6xv1-1"
})(["display:flex;flex-direction:column;flex:1;min-height:0;gap:", ";overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;overflow-anchor:none;padding:", " ", " ", ";::-webkit-scrollbar{width:6px;}::-webkit-scrollbar-track{border-radius:3px;}::-webkit-scrollbar-thumb{background-color:#c4ccd7;border-radius:3px;}::-webkit-scrollbar-thumb:hover{background-color:#a8b3c1;}scrollbar-width:thin;scrollbar-color:#c4ccd7 #e9edf2;"], getSize('padding4'), getSize('padding16'), getSize('padding8'), getSize('padding32'));
export var SectionSummaryPanelV2Component = function SectionSummaryPanelV2Component(_ref) {
  var selectedFormItemId = _ref.selectedFormItemId,
      sectionSummaries = _ref.sectionSummaries,
      questions = _ref.questions,
      formTotalQuestions = _ref.formTotalQuestions,
      formTotalScore = _ref.formTotalScore,
      hasUnsavedQuestionOrSectionChanges = _ref.hasUnsavedQuestionOrSectionChanges,
      onSelectQuestion = _ref.onSelectQuestion,
      onSelectedSection = _ref.onSelectedSection,
      sectionFocusedId = _ref.sectionFocusedId,
      labelTranslation = _ref.labelTranslation,
      moveQuestion = _ref.moveQuestion,
      moveQuestionToSection = _ref.moveQuestionToSection,
      headings = _ref.headings;
  var wrapperRef = useRef(null);
  useDragAutoScroll(wrapperRef);
  useEffect(function () {
    if (sectionFocusedId && wrapperRef.current) {
      var sectionElement = document.getElementById("".concat(sectionFocusedId, "-panel"));

      if (sectionElement) {
        var timeoutId = setTimeout(function () {
          sectionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }, 100);
        return function () {
          return clearTimeout(timeoutId);
        };
      }
    }
  }, [sectionFocusedId]);
  var sectionItems = useMemo(function () {
    if (!(sectionSummaries !== null && sectionSummaries !== void 0 && sectionSummaries.length)) {
      return [];
    } // Same as center editor: sort globally by section order, then filter —
    // preserves continuous titleIndex (Section 3 must not restart at 1).


    var sortedQuestions = Questions.sortQuestionsWithIndexAndSection(questions, sectionSummaries.map(function (summary) {
      return summary.id.toString();
    }));
    return sectionSummaries.map(function (section, sectionIndex) {
      var _Headings$sortWithQue;

      var sectionId = section.id.toString();
      var sectionQuestions = sortedQuestions.filter(function (q) {
        return q.sectionId === sectionId;
      });
      var sectionHeadings = Headings === null || Headings === void 0 ? void 0 : Headings.sort(headings === null || headings === void 0 ? void 0 : headings.filter(function (h) {
        return h.sectionId === sectionId;
      }));
      var items = (_Headings$sortWithQue = Headings === null || Headings === void 0 ? void 0 : Headings.sortWithQuestions(sectionHeadings, sectionQuestions)) !== null && _Headings$sortWithQue !== void 0 ? _Headings$sortWithQue : sectionQuestions;
      return {
        section: section,
        sectionId: sectionId,
        sectionIndex: sectionIndex,
        items: items
      };
    });
  }, [sectionSummaries, questions, headings]);
  var handleMoveQuestion = useCallback(function (dragIndex, hoverIndex, sectionId) {
    moveQuestion === null || moveQuestion === void 0 ? void 0 : moveQuestion(dragIndex, hoverIndex, sectionId);
  }, [moveQuestion]);
  var allInterleavedItems = useMemo(function () {
    return sectionItems.flatMap(function (entry) {
      return entry.items;
    });
  }, [sectionItems]);
  return /*#__PURE__*/_jsxs(Layout, {
    children: [/*#__PURE__*/_jsx(HeaderComponent, {
      pointIcon: /*#__PURE__*/_jsx(Image, {
        src: "/icons/trophy.svg",
        width: 16,
        height: 16,
        alt: "trophy"
      }),
      totalQuestionScore: formTotalScore,
      totalQuestions: formTotalQuestions,
      labelTranslation: labelTranslation
    }), /*#__PURE__*/_jsx(StyledSectionSummaryWrapper, {
      ref: wrapperRef,
      children: !sectionSummaries ? /*#__PURE__*/_jsx(ParagraphSkeletonLoading, {
        rows: 10
      }) : /*#__PURE__*/_jsxs(SidebarLiveDragProvider, {
        allItems: allInterleavedItems,
        children: [/*#__PURE__*/_jsx(SidebarLiveDragCommitBridge, {
          onMoveQuestionToSection: moveQuestionToSection
        }), sectionItems.map(function (_ref2) {
          var section = _ref2.section,
              sectionId = _ref2.sectionId,
              sectionIndex = _ref2.sectionIndex,
              items = _ref2.items;
          return /*#__PURE__*/_jsx(SectionItemComponent, {
            section: section,
            sectionIndex: sectionIndex,
            questions: items,
            selectedFormItemId: selectedFormItemId,
            sectionFocusedId: sectionFocusedId,
            hasUnsavedQuestionOrSectionChanges: hasUnsavedQuestionOrSectionChanges,
            onSelectedSection: onSelectedSection,
            onSelectQuestion: onSelectQuestion,
            labelTranslation: labelTranslation,
            sectionId: sectionId,
            onMoveQuestion: moveQuestion ? handleMoveQuestion : undefined,
            onMoveQuestionToSection: moveQuestionToSection
          }, sectionId);
        })]
      })
    })]
  });
};
/** Wires drop-target commits to the container move handler. */

var SidebarLiveDragCommitBridge = function SidebarLiveDragCommitBridge(_ref3) {
  var onMoveQuestionToSection = _ref3.onMoveQuestionToSection;

  var _useSidebarLiveDrag = useSidebarLiveDrag(),
      registerCrossSectionCommit = _useSidebarLiveDrag.registerCrossSectionCommit;

  var handlerRef = useRef(onMoveQuestionToSection);
  handlerRef.current = onMoveQuestionToSection;
  useEffect(function () {
    var handler = function handler(args) {
      var _handlerRef$current;

      (_handlerRef$current = handlerRef.current) === null || _handlerRef$current === void 0 ? void 0 : _handlerRef$current.call(handlerRef, args.questionId, args.targetSectionId, args.targetIndex, args.sourceSectionId);
    };

    registerCrossSectionCommit(handler);
    return function () {
      return registerCrossSectionCommit(null);
    };
  }, [registerCrossSectionCommit]);
  return null;
};